import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Heading, Text, Badge, Flex, Spinner, Alert, AlertIcon,
  Tabs, TabList, TabPanels, Tab, TabPanel,
  Table, Thead, Tbody, Tr, Th, Td,
  Stat, StatLabel, StatNumber, StatGroup,
  Input, Select, Button, HStack, SimpleGrid, Code, Icon,
  Progress, Textarea, Tooltip, useToast,
} from '@chakra-ui/react';
import { FiRefreshCw, FiTerminal, FiDownload } from 'react-icons/fi';

const API_BASE = '/api/v1';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Token ${localStorage.getItem('token')}`,
});

const PostgresStatus = () => {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [tables, setTables] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [tablePage, setTablePage] = useState(1);
  const [tableSort, setTableSort] = useState('size');
  const [tableSearch, setTableSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Query state
  const [querySql, setQuerySql] = useState('SELECT table_name, pg_size_pretty(pg_total_relation_size(quote_ident(table_name))) AS size\nFROM information_schema.tables\nWHERE table_schema = \'public\'\nORDER BY pg_total_relation_size(quote_ident(table_name)) DESC\nLIMIT 20;');
  const [queryResult, setQueryResult] = useState(null);
  const [queryRunning, setQueryRunning] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/postgres/status/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) setStatus(data.postgres);
      else setError(data.message || 'Failed to fetch status');
    } catch (e) { setError(e.message); }
  }, []);

  const fetchTables = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/postgres/tables/?page=${tablePage}&sort=${tableSort}&search=${tableSearch}`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) setTables(data);
      else setError(data.message);
    } catch (e) { setError(e.message); }
  }, [tablePage, tableSort, tableSearch]);

  const fetchActivity = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/postgres/activity/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) setActivity(data.activity);
      else setError(data.message);
    } catch (e) { setError(e.message); }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([
      fetchStatus(),
      tabIndex === 1 ? fetchTables() : Promise.resolve(),
      tabIndex === 2 ? fetchActivity() : Promise.resolve(),
    ]);
    setLoading(false);
  }, [fetchStatus, fetchTables, fetchActivity, tabIndex]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(refreshAll, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshAll]);

  useEffect(() => {
    if (tabIndex === 1) fetchTables();
    if (tabIndex === 2) fetchActivity();
  }, [tabIndex, fetchTables, fetchActivity]);

  const formatUptime = (startTime) => {
    if (!startTime) return 'N/A';
    const start = new Date(startTime);
    const now = new Date();
    const diff = Math.floor((now - start) / 1000);
    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const executeQuery = async () => {
    if (!querySql.trim()) return;
    setQueryRunning(true);
    setQueryResult(null);
    try {
      const res = await fetch(`${API_BASE}/postgres/query/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ sql: querySql }),
      });
      const data = await res.json();
      setQueryResult(data);
      if (!data.success) {
        toast({ title: data.message || 'Query failed', status: 'error', duration: 5000 });
      }
    } catch (e) {
      toast({ title: 'Error executing query', status: 'error', duration: 3000 });
    }
    setQueryRunning(false);
  };

  const downloadCsv = () => {
    if (!queryResult?.rows || queryResult.rows.length === 0) return;
    const cols = queryResult.columns;
    const escapeCsv = (val) => {
      if (val === null || val === undefined) return '';
      const s = String(val);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const header = cols.map(escapeCsv).join(',');
    const rows = queryResult.rows.map(row => cols.map(c => escapeCsv(row[c])).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `query_results_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">PostgreSQL Status</Heading>
          <Text color="gray.500">Monitor database health, tables, and active queries</Text>
        </Box>
        <HStack>
          <Button size="sm" variant={autoRefresh ? 'solid' : 'outline'} colorScheme="purple"
            onClick={() => setAutoRefresh(!autoRefresh)}>
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button size="sm" leftIcon={<Icon as={FiRefreshCw} />} onClick={refreshAll} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </Flex>

      {error && (
        <Alert status="error" mb={4} borderRadius="md">
          <AlertIcon />{error}
        </Alert>
      )}

      {status && status.error && (
        <Alert status="warning" mb={4} borderRadius="md">
          <AlertIcon />{status.error}
        </Alert>
      )}

      <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="purple">
        <TabList>
          <Tab>Overview</Tab>
          <Tab>Tables</Tab>
          <Tab>Active Queries</Tab>
          <Tab><HStack spacing={2}><FiTerminal size={14} /><Text>Query</Text></HStack></Tab>
        </TabList>

        <TabPanels>
          {/* Overview Tab */}
          <TabPanel px={0}>
            {loading && !status ? <Spinner size="xl" /> : status && status.connected ? (
              <>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
                  <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
                    <StatGroup><Stat>
                      <StatLabel>Server Version</StatLabel>
                      <StatNumber fontSize="lg">{status.server_info?.version}</StatNumber>
                    </Stat></StatGroup>
                  </Box>
                  <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
                    <StatGroup><Stat>
                      <StatLabel>Uptime</StatLabel>
                      <StatNumber fontSize="lg">{formatUptime(status.server_info?.start_time)}</StatNumber>
                    </Stat></StatGroup>
                  </Box>
                  <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
                    <StatGroup><Stat>
                      <StatLabel>Database Size</StatLabel>
                      <StatNumber fontSize="lg">{status.database?.size_pretty}</StatNumber>
                    </Stat></StatGroup>
                  </Box>
                  <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
                    <StatGroup><Stat>
                      <StatLabel>Latency</StatLabel>
                      <StatNumber fontSize="lg">{status.server_info?.latency_ms} ms</StatNumber>
                    </Stat></StatGroup>
                  </Box>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
                  <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
                    <Heading size="sm" mb={3}>Connections</Heading>
                    <Text>Active: <Badge colorScheme="green">{status.connections?.active}</Badge></Text>
                    <Text>Idle: <Badge colorScheme="yellow">{status.connections?.idle}</Badge></Text>
                    <Text>Total: <Badge colorScheme="blue">{status.connections?.total}</Badge> / {status.connections?.max}</Text>
                    <Progress mt={2} value={(status.connections?.total / status.connections?.max) * 100}
                      colorScheme="purple" borderRadius="full" size="sm" />
                  </Box>
                  <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
                    <Heading size="sm" mb={3}>Configuration</Heading>
                    {status.config && Object.entries(status.config).map(([k, v]) => (
                      <Flex key={k} justify="space-between" py={1} borderBottom="1px solid" borderColor="gray.100">
                        <Code fontSize="sm">{k}</Code><Text fontSize="sm">{v}</Text>
                      </Flex>
                    ))}
                  </Box>
                </SimpleGrid>

                <Box bg="white" p={5} borderRadius="lg" boxShadow="md">
                  <Heading size="sm" mb={3}>Database Info</Heading>
                  <Text>Name: <strong>{status.database?.name}</strong></Text>
                  <Text>Tables: <strong>{status.tables?.count}</strong></Text>
                </Box>
              </>
            ) : !loading && <Text color="gray.500">PostgreSQL is not connected.</Text>}
          </TabPanel>

          {/* Tables Tab */}
          <TabPanel px={0}>
            <HStack mb={4}>
              <Input placeholder="Search tables..." value={tableSearch}
                onChange={(e) => { setTableSearch(e.target.value); setTablePage(1); }} maxW="300px" />
              <Select value={tableSort} onChange={(e) => setTableSort(e.target.value)} maxW="200px">
                <option value="size">Sort by Size</option>
                <option value="name">Sort by Name</option>
                <option value="rows">Sort by Rows</option>
              </Select>
            </HStack>
            {tables ? (
              <>
                <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
                  <Table size="sm">
                    <Thead><Tr>
                      <Th>Table</Th><Th>Size</Th><Th isNumeric>Rows (est)</Th>
                      <Th>Last Vacuum</Th><Th>Last Analyze</Th>
                    </Tr></Thead>
                    <Tbody>
                      {tables.tables?.map((t) => (
                        <Tr key={t.tablename}>
                          <Td><Code fontSize="sm">{t.tablename}</Code></Td>
                          <Td>{t.size_pretty}</Td>
                          <Td isNumeric>{Number(t.row_estimate).toLocaleString()}</Td>
                          <Td fontSize="xs">{t.last_vacuum === 'Never' ? <Badge colorScheme="red" fontSize="xs">Never</Badge> : new Date(t.last_vacuum).toLocaleDateString()}</Td>
                          <Td fontSize="xs">{t.last_analyze === 'Never' ? <Badge colorScheme="red" fontSize="xs">Never</Badge> : new Date(t.last_analyze).toLocaleDateString()}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
                {tables.pagination && (
                  <HStack mt={4} justify="center">
                    <Button size="sm" onClick={() => setTablePage(p => Math.max(1, p - 1))} isDisabled={tablePage <= 1}>Prev</Button>
                    <Text fontSize="sm">Page {tables.pagination.page} of {tables.pagination.pages}</Text>
                    <Button size="sm" onClick={() => setTablePage(p => p + 1)} isDisabled={tablePage >= tables.pagination.pages}>Next</Button>
                  </HStack>
                )}
              </>
            ) : <Spinner />}
          </TabPanel>

          {/* Activity Tab */}
          <TabPanel px={0}>
            {activity ? (
              <Box bg="white" borderRadius="lg" boxShadow="md" overflowX="auto">
                <Table size="sm">
                  <Thead><Tr>
                    <Th>PID</Th><Th>User</Th><Th>State</Th><Th>Duration</Th><Th>Query</Th>
                  </Tr></Thead>
                  <Tbody>
                    {activity.map((a) => (
                      <Tr key={a.pid}>
                        <Td>{a.pid}</Td>
                        <Td>{a.usename}</Td>
                        <Td><Badge colorScheme={a.state === 'active' ? 'green' : a.state === 'idle' ? 'yellow' : 'gray'}>{a.state}</Badge></Td>
                        <Td>{a.query_duration_secs != null ? `${a.query_duration_secs}s` : '-'}</Td>
                        <Td maxW="400px" isTruncated><Code fontSize="xs">{a.query}</Code></Td>
                      </Tr>
                    ))}
                    {activity.length === 0 && <Tr><Td colSpan={5} textAlign="center" color="gray.500">No active connections</Td></Tr>}
                  </Tbody>
                </Table>
              </Box>
            ) : <Spinner />}
          </TabPanel>

          {/* Query Tab */}
          <TabPanel px={0}>
            <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={4}>
              <HStack spacing={2} mb={4}>
                <FiTerminal />
                <Heading size="md">SQL Query</Heading>
                <Badge colorScheme="yellow" fontSize="xs">Read-only</Badge>
              </HStack>
              <Textarea
                value={querySql}
                onChange={(e) => setQuerySql(e.target.value)}
                placeholder="SELECT * FROM ..."
                fontFamily="mono"
                fontSize="sm"
                minH="150px"
                bg="gray.900"
                color="green.300"
                border="none"
                _placeholder={{ color: 'gray.500' }}
                _focus={{ border: 'none', boxShadow: 'none' }}
                mb={3}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    executeQuery();
                  }
                }}
              />
              <HStack justify="space-between">
                <Text fontSize="xs" color="gray.500">⌘+Enter to execute. Write operations (DROP, DELETE, INSERT, UPDATE, etc.) are blocked.</Text>
                <HStack spacing={2}>
                  {queryResult?.rows && queryResult.rows.length > 0 && (
                    <Button leftIcon={<FiDownload />} size="sm" colorScheme="green" variant="outline" onClick={downloadCsv}>
                      Download CSV
                    </Button>
                  )}
                  <Button colorScheme="blue" size="sm" onClick={executeQuery} isLoading={queryRunning} loadingText="Running...">
                    Execute
                  </Button>
                </HStack>
              </HStack>
            </Box>

            {queryResult && (
              <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
                <HStack p={4} bg={queryResult.success ? 'green.50' : 'red.50'} borderBottom="1px" borderColor="gray.200" justify="space-between">
                  <HStack spacing={3}>
                    <Badge colorScheme={queryResult.success ? 'green' : 'red'}>
                      {queryResult.success ? 'Success' : 'Error'}
                    </Badge>
                    {queryResult.success && (
                      <>
                        <Text fontSize="sm" color="gray.600">{queryResult.row_count} row{queryResult.row_count !== 1 ? 's' : ''}</Text>
                        <Text fontSize="sm" color="gray.500">•</Text>
                        <Text fontSize="sm" color="gray.600">{queryResult.duration_ms} ms</Text>
                      </>
                    )}
                  </HStack>
                  {queryResult.rows && queryResult.rows.length > 0 && (
                    <Button leftIcon={<FiDownload />} size="xs" colorScheme="green" variant="ghost" onClick={downloadCsv}>CSV</Button>
                  )}
                </HStack>

                {!queryResult.success ? (
                  <Box p={4}>
                    <Code colorScheme="red" fontSize="sm" whiteSpace="pre-wrap">{queryResult.message}</Code>
                  </Box>
                ) : queryResult.columns && queryResult.columns.length > 0 ? (
                  <Box overflowX="auto" maxH="500px" overflowY="auto">
                    <Table size="sm">
                      <Thead position="sticky" top={0} bg="white" zIndex={1}>
                        <Tr>
                          {queryResult.columns.map((col, i) => (
                            <Th key={i} fontSize="xs">{col}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {queryResult.rows.map((row, ri) => (
                          <Tr key={ri} _hover={{ bg: 'gray.50' }}>
                            {queryResult.columns.map((col, ci) => (
                              <Td key={ci} fontSize="xs" maxW="300px">
                                <Tooltip label={String(row[col] ?? '')} placement="top-start">
                                  <Text isTruncated>{row[col] === null ? <Text as="span" color="gray.400" fontStyle="italic">NULL</Text> : String(row[col])}</Text>
                                </Tooltip>
                              </Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ) : (
                  <Box p={4} textAlign="center">
                    <Text color="gray.500" fontSize="sm">Query executed successfully (no rows returned)</Text>
                  </Box>
                )}
              </Box>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default PostgresStatus;
